import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { T, useLang } from "@/i18n";
import { LangSwitcher } from "@/components/LangSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY_URL = "https://calendly.com/trust-master/30min";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const [lang, setLang] = useLang();
  const t = T[lang].success;
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setInfo(data);
          setStatus("paid");
          return;
        }
      } catch (e) {
        if (attempts > 2) {
          setStatus("error");
          return;
        }
      }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="bg-[#200910] text-[#faf6ef] min-h-screen font-sans flex items-center justify-center px-6">
      <div className="max-w-xl w-full border border-[#4d1a2a] bg-[#33101c] p-10" data-testid="payment-success-page">
        <div className="flex justify-end mb-6"><LangSwitcher lang={lang} setLang={setLang} /></div>
        {status === "checking" && (
          <div data-testid="payment-checking">
            <span className="font-mono-lbl text-xs uppercase tracking-[0.18em] text-[#d3b870]">{t.checking_k}</span>
            <h1 className="font-display uppercase text-3xl mt-3">{t.checking_t}</h1>
            <p className="text-sm text-[#b4939c] mt-4">{t.checking_d}</p>
          </div>
        )}

        {status === "paid" && (
          <div data-testid="payment-confirmed">
            <span className="font-mono-lbl text-xs uppercase tracking-[0.18em] text-[#d3b870]">{t.paid_k}</span>
            <h1 className="font-display uppercase text-3xl mt-3">{t.paid_t}</h1>
            <p className="text-sm text-[#b4939c] mt-4">
              {info?.package_name} {t.paid_suffix}
              {info?.email_sent ? t.paid_email_sent : t.paid_email_pending}
            </p>
            <div className="mt-8 space-y-4">
              <div className="border-t-2 border-[#d3b870] pt-4">
                <span className="font-mono-lbl text-xs text-[#d3b870]">{t.step1_k}</span>
                <p className="text-sm text-[#b4939c] mt-1 mb-3">{t.step1_d}</p>
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="calendly-booking-link"
                  className="inline-block px-6 py-3 bg-[#d3b870] text-[#200910] font-display uppercase tracking-wider text-sm hover:bg-[#ecd9ab] transition-colors">
                  {t.step1_btn}
                </a>
              </div>
              <div className="border-t-2 border-[#4d1a2a] pt-4">
                <span className="font-mono-lbl text-xs text-[#b4939c]">{t.step2_k}</span>
                <p className="text-sm text-[#b4939c] mt-1 mb-3">{t.step2_d}</p>
                <Link to={`/incarico?session_id=${sessionId}`} data-testid="mandate-link"
                  className="inline-block px-6 py-3 border border-[#d3b870] text-[#d3b870] font-mono-lbl uppercase tracking-[0.14em] text-xs hover:bg-[#d3b870] hover:text-[#200910] transition-colors">
                  {t.step2_btn}
                </Link>
              </div>
              <div className="border-t-2 border-[#4d1a2a] pt-4">
                <span className="font-mono-lbl text-xs text-[#b4939c]">{t.step3_k}</span>
                <p className="text-sm text-[#b4939c] mt-1 mb-3">{t.step3_d}</p>
                <Link to={`/questionario?session_id=${sessionId}`} data-testid="questionnaire-link"
                  className="inline-block px-6 py-3 border border-[#d3b870] text-[#d3b870] font-mono-lbl uppercase tracking-[0.14em] text-xs hover:bg-[#d3b870] hover:text-[#200910] transition-colors">
                  {t.step3_btn}
                </Link>
              </div>
            </div>
          </div>
        )}

        {(status === "error" || status === "timeout") && (
          <div data-testid="payment-error">
            <span className="font-mono-lbl text-xs uppercase tracking-[0.18em] text-[#d3b870]">{t.err_k}</span>
            <h1 className="font-display uppercase text-3xl mt-3">{t.err_t}</h1>
            <p className="text-sm text-[#b4939c] mt-4">{t.err_d}</p>
            <Link to="/" data-testid="back-home-link" className="inline-block mt-6 px-6 py-3 border border-[#d3b870] text-[#d3b870] font-mono-lbl uppercase tracking-[0.14em] text-xs hover:bg-[#d3b870] hover:text-[#200910] transition-colors">
              {t.err_btn}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
